/** Persisted on the outbox item: remounting or editing must not reset a conflict. */
export interface SyncFailureState {
  attempts?: number;
  lastError?: string;
  lastErrorCode?: string;
  blocked?: boolean;
  nextAttemptAt?: number;
}

export function isRevisionConflict(state: SyncFailureState): boolean {
  return ["40001", "PT409"].includes(state.lastErrorCode ?? "") || /revision conflict/i.test(state.lastError ?? "");
}

export function isSyncBlocked(state: SyncFailureState): boolean {
  return Boolean(state.blocked || (state.attempts ?? 0) >= 5 || isRevisionConflict(state));
}

export function recordSyncFailure(state: SyncFailureState, error: unknown, now = Date.now()): SyncFailureState {
  const detail = error && typeof error === "object" ? error as { message?: unknown; code?: unknown } : {};
  const attempts = (state.attempts ?? 0) + 1;
  const result: SyncFailureState = {
    attempts, lastError: typeof detail.message === "string" ? detail.message : String(error),
    lastErrorCode: typeof detail.code === "string" ? detail.code : "",
    nextAttemptAt: now + Math.min(60_000, 5_000 * 2 ** Math.min(attempts - 1, 4)),
  };
  result.blocked = isSyncBlocked(state) || isSyncBlocked(result);
  return result;
}

export function inheritedSyncFailure(states: readonly SyncFailureState[]): SyncFailureState {
  const strongest = [...states].sort((a, b) => Number(isSyncBlocked(b)) - Number(isSyncBlocked(a)) || (b.attempts ?? 0) - (a.attempts ?? 0))[0];
  if (!strongest) return {};
  return { attempts: strongest.attempts ?? 0, lastError: strongest.lastError ?? "", lastErrorCode: strongest.lastErrorCode ?? "",
    blocked: isSyncBlocked(strongest), nextAttemptAt: Math.max(0, ...states.map(state => state.nextAttemptAt ?? 0)) };
}
